import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  ALLOWED_ATTACHMENT_MIME_TYPES,
  UnifiedAttachment,
} from '@this+that/models';
import log from 'loglevel';

import { useCustomToast } from './useCustomToast';
import { MAX_FILE_SIZE_BYTES } from '../utils/constants';
import {
  toMegabytes,
  validateUploadFile,
} from '../utils/fileHandlers/presignedUpload';

export interface FileAttachment extends UnifiedAttachment {
  file: File;
  s3Key?: string;
  size?: number;
}

export const formatAttachmentsForApi = (
  attachments: FileAttachment[],
): UnifiedAttachment[] =>
  attachments.map(({ name, contentType, contentBytes }) => ({
    name,
    contentType,
    contentBytes,
  }));

export interface ControlledFileAttachments {
  attachments: FileAttachment[];
  setAttachments: Dispatch<SetStateAction<FileAttachment[]>>;
}

interface UseFileAttachmentsReturn {
  attachments: FileAttachment[];
  isLoading: boolean;
  addFiles: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
  removeFile: (index: number) => void;
  clearAttachments: () => void;
  replaceAttachments: (next: FileAttachment[]) => void;
}

const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = (reader.result as string).split(',')[1];
      resolve(base64String);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const sumAttachmentBytes = (attachments: FileAttachment[]): number =>
  attachments.reduce(
    (total, attachment) => total + (attachment.size ?? attachment.file.size),
    0,
  );

interface UseFileAttachmentsOptions {
  // When set, caps the combined size and replaces the per-file MAX_FILE_SIZE_BYTES.
  maxTotalBytes?: number;
  controlled?: ControlledFileAttachments;
}

export const useFileAttachments = (
  initialAttachments?: FileAttachment[],
  { maxTotalBytes, controlled }: UseFileAttachmentsOptions = {},
): UseFileAttachmentsReturn => {
  const { t } = useTranslation();
  const { errorMessage } = useCustomToast();

  const [internalAttachments, setInternalAttachments] = useState<
    FileAttachment[]
  >([]);
  const attachments = controlled?.attachments ?? internalAttachments;
  const setAttachments = controlled?.setAttachments ?? setInternalAttachments;
  const isControlled = !!controlled;
  const [isLoading, setIsLoading] = useState(false);

  const addFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    setIsLoading(true);
    try {
      const newAttachments: FileAttachment[] = [];
      const fileArray = Array.from(files);
      let totalBytes = sumAttachmentBytes(attachments);

      for (const file of fileArray) {
        if (
          !validateUploadFile(
            file,
            ALLOWED_ATTACHMENT_MIME_TYPES,
            maxTotalBytes ?? MAX_FILE_SIZE_BYTES,
            t,
            errorMessage,
          )
        ) {
          continue;
        }

        if (
          maxTotalBytes !== undefined &&
          totalBytes + file.size > maxTotalBytes
        ) {
          errorMessage(
            t('attachments.errors.totalTooLarge', {
              maxSize: toMegabytes(maxTotalBytes),
            }),
          );
          continue;
        }

        const isDuplicate = attachments.some(
          (attachment) =>
            attachment.name === file.name &&
            attachment.contentType === file.type,
        );

        if (isDuplicate) {
          errorMessage(
            t('attachments.errors.fileAlreadyAttached', {
              fileName: file.name,
            }),
          );
          continue;
        }

        try {
          const base64Content = await convertFileToBase64(file);

          const attachment: FileAttachment = {
            name: file.name,
            contentType: file.type,
            contentBytes: base64Content,
            file: file,
          };

          newAttachments.push(attachment);
          totalBytes += file.size;
        } catch (error) {
          log.error(`Error processing file ${file.name}:`, error);
          errorMessage(
            t('attachments.errors.failedToProcessFile', {
              fileName: file.name,
            }),
          );
        }
      }

      if (newAttachments.length > 0) {
        setAttachments((prev) => [...prev, ...newAttachments]);
      }
    } catch (error) {
      log.error('Error adding files:', error);
      errorMessage(t('attachments.errors.failedToAddFiles'));
    } finally {
      setIsLoading(false);
      event.target.value = '';
    }
  };

  const removeFile = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const clearAttachments = () => {
    setAttachments([]);
  };

  const replaceAttachments = (next: FileAttachment[]) => {
    setAttachments(next);
  };

  useEffect(() => {
    if (!isControlled && initialAttachments && initialAttachments.length > 0) {
      setInternalAttachments(initialAttachments);
    }
  }, [initialAttachments, isControlled]);

  return {
    attachments,
    isLoading,
    addFiles,
    removeFile,
    clearAttachments,
    replaceAttachments,
  };
};
